import { NumberField, Label } from '@heroui/react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  Path
} from 'react-hook-form';
import { useMemo } from 'react';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type DisplayNumberInputProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  accessor?: (value: unknown) => number | null | undefined;
  empty?: string;
  formatOptions?: Intl.NumberFormatOptions;
  path: Path<T>;
  label?: string;
  className?: string;
  [key: string]: unknown;
};

export const DisplayNumberInputBase = <T extends FieldValues = FieldValues>({
  accessor,
  path,
  rules,
  shouldUnregister,
  defaultValue,
  disabled,
  label,
  className,
  ...props
}: DisplayNumberInputProps<T>) => {
  const {
    field: { onChange, value: fieldValue, ...fieldProps }
  } = useController<T>({
    name: path,
    rules,
    shouldUnregister,
    defaultValue,
    disabled
  });

  const value = useMemo(
    () => (accessor ? accessor(fieldValue) : fieldValue) ?? NaN,
    [accessor, fieldValue]
  );

  return (
    <NumberField
      {...fieldProps}
      isReadOnly
      value={value}
      onChange={(val) => onChange(val)}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <NumberField.Group className="grid-cols-1">
        <NumberField.Input />
      </NumberField.Group>
    </NumberField>
  );
};

export const DisplayNumberInput = <T extends FieldValues = FieldValues>(
  props: DisplayNumberInputProps<T>
) => useGlobalComponent('DisplayNumberInput', DisplayNumberInputBase, props);
